/*
 * Ryan O'Dowd
 * 2024-09-28
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import Button from '@components/common/Button';
import CPassage from '@classes/Passage';
// @TODO: import Checkbox from '@components/common/Checkbox';
import Dialog from '@components/common/Dialog';
import LinearProgress from '@components/common/LinearProgress';
import React from 'react';
import Table from '@components/common/Table';
import Tooltip from '@components/common/Tooltip';
import {
  View,
} from 'react-native';
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const StudentAssignmentsDialog = (props: StudentAssignmentsDialogProps) => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();
  const allUserAssignmentProgress = hooks.useTypedSelector((state) => state.goalProgress[props.goal.id])?.[props.studentId] || {};

  const classroomStudents = hooks.useTypedSelector((state) => state.classroomStudents[props.goal.classroom_goal_metadata.classroom_id] || []);
  const student = classroomStudents.find((cs) => +(cs.user_id || cs.user?.id) === +props.studentId);
  const studentName = student?.display_name || student?.user?.display_name;

  const assignmentsTableData = props.goal.modules.flatMap((module) => module.assignments).map((assignment, index) => {
    const userAssignmentProgress = allUserAssignmentProgress[assignment.id];
    const numLessonsCompleted = userAssignmentProgress.nlc;
    const numLessonsRequired = userAssignmentProgress.nlr;
    const progress = numLessonsCompleted / numLessonsRequired;
    /* @TODO: would be great to click on any value to see it plotted against rest of class to see outliers
      start/end date is a combined graph with ranges
    */
    return [ // @TODO: update ids
      {
        id: `${assignment.id}_name`,
        content: (
          <span>
            {/* @TODO:
            <Checkbox
              isChecked={!!_checkboxStatuses[cs.id]}
              onPress={() => {
                setCheckboxStatuses((curr) => {
                  const next = {...curr};
                  next[cs.id] = !curr[cs.id];
                  return next;
                });
              }}
            />
            */}
            {['verse_memory', 'reading'].includes(assignment.assignment_type)
              ? (
                <span style={styles.nameWrapper}>
                  <span style={styles.mainTitle}>{CPassage.createFromObject(assignment.assignment_object.passage).prettyPassageName()}</span>{/* @TODO: change tag type? and styles */}
                  <span style={styles.subTitle}>{assignment.name}</span>{/* @TODO: change tag type? and styles */}
                </span>
              )
              : (
                <React.Fragment>
                  <span style={styles.mainTitle}>{assignment.name}</span>{/* @TODO: change tag type? and styles */}
                </React.Fragment>
              )
            }
          </span>
        ),
        sortValue: index + 1,
      }, // @TODO: maybe surround with join date (or invitation date if pending)
      // @TODO: {id: `${assignment.id}_date_started`, content: userAssignmentProgress.ds ? <Tooltip title={dateFnsFormat(userAssignmentProgress.ds, 'MMM d, yyyy, h:mm a')}><span>{dateFnsFormat(userAssignmentProgress.ds, 'MMM d')}</span></Tooltip> : '-'}, // @TODO: replace '-' with tooltip about joining via public code?
      // @TODO: {id: `${assignment.id}_date_completed`, content: userAssignmentProgress.dc ? <Tooltip title={dateFnsFormat(userAssignmentProgress.dc, 'MMM d, yyyy, h:mm a')}><span>{dateFnsFormat(userAssignmentProgress.dc, 'MMM d')}</span></Tooltip> : '-'},
      /* @TODO: combine questions answered/skiped and time into a "more info" button
        should show cell data as warning or okay flag based on...
          num skipped
          answer/skip ratio
          total time spent
          should "normal" be decided by class average or an objective metric? (e.g. half the time of the average vs. less than 1s per 10 words)
      */
      // @TODO: {id: `${assignment.id}_total_num_questions_answered`, content: userAssignmentProgress.total_num_questions_answered},
      // @TODO: {id: `${assignment.id}_total_num_questions_skipped`, content: userAssignmentProgress.total_num_questions_skipped},
      {
        id: `${assignment.id}_date_started`,
        content: userAssignmentProgress.ds ? <Tooltip title={dateFnsFormat(userAssignmentProgress.ds, 'MMM d, yyyy, h:mm a')}><span>{dateFnsFormat(userAssignmentProgress.ds, 'MMM d')}</span></Tooltip> : '-',
        sortValue: new Date(userAssignmentProgress.ds || 0).getTime(),
      },
      {
        id: `${assignment.id}_date_completed`,
        content: userAssignmentProgress.dc ? <Tooltip title={dateFnsFormat(userAssignmentProgress.dc, 'MMM d, yyyy, h:mm a')}><span>{dateFnsFormat(userAssignmentProgress.dc, 'MMM d')}</span></Tooltip> : '-',
        sortValue: new Date(userAssignmentProgress.dc || 0).getTime(),
      },
      {
        id: `${assignment.id}_date_last_activity`,
        content: userAssignmentProgress.dla ? <Tooltip title={dateFnsFormat(userAssignmentProgress.dla, 'MMM d, yyyy, h:mm a')}><span>{dateFnsFormat(userAssignmentProgress.dla, 'MMM d')}</span></Tooltip> : '-',
        sortValue: new Date(userAssignmentProgress.dla || 0).getTime(),
      },
      {
        id: `${assignment.id}_total_milliseconds_spent`,
        content: !userAssignmentProgress.t ? '-' : utilities.humanReadableTimeExact(userAssignmentProgress.t), // @TODO: color code for more than one standard deviation away from mean
        sortValue: userAssignmentProgress.t,
      },
      {
        id: `${assignment.id}_progress`,
        content: progress === 0 ? '-' : (
          <Tooltip title={`${numLessonsCompleted} of ${numLessonsRequired} lessons completed`}>{/* @TODO: if it's a reading, lecture, or memory only (no lessons requried) then don't show 0/1 or 1/1...just show "not started" or "completed"? or show checkmark for memorized */}{/* @TODO: too much padding */}
            <View style={styles.progressWrapper}>
              <LinearProgress progress={progress} />
            </View>
          </Tooltip>
        ),
        sortValue: progress,
      },
    ];
  });

  return (
    <Dialog
      visible={true}
      onDismiss={() => props.onClose()} // @TODO: ask about unsaved changes
    >
      <Dialog.Title>{studentName}</Dialog.Title>
      <Dialog.Content>
        <Table// @TODO: combine with AssignmentProgressDialog?
          tableTitle='Assignment progress'
          open={true}
          disableAccordion={true}
          headerRowData={[
            {width: 200, id: 'assignment_name', content: 'Assignment name'},
            {width: 100, id: 'date_started', content: 'Date started', rightAlign: true},
            {width: 100, id: 'date_completed', content: 'Date completed', rightAlign: true},
            {width: 100, id: 'last_activity', content: 'Last activity', rightAlign: true},
            {width: 100, id: 'total_milliseconds_spent', content: 'Total time spent', rightAlign: true},
            // @TODO: 'Questions answered',
            // @TODO: 'Questions skipped',
            {width: 80, id: 'progress', content: 'Progress', rightAlign: true},
          ]}// @TODO: some of these should be named better and/or have tooltips
          isSortable={true}
          orderByIndex={0}
          orderDirection='ascending'
          rowData={assignmentsTableData}
        />
      </Dialog.Content>
      <Dialog.Actions>
        <Button onPress={() => props.onClose()}>
          {i18n('close')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

class StudentAssignmentsDialogProps {
  onClose: () => void;
  goal: any;
  studentId: number;
};

export default StudentAssignmentsDialog;
